import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TablePagination,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Button,
    Paper,
    Divider,
    Stack,
    useMediaQuery,
    Card,
    CardContent,
} from "@mui/material";
import { Add, Remove, Delete } from "@mui/icons-material";
import CheckoutModal from "./CheckoutModal";
import { useDispatch, useSelector } from "react-redux";
import { removeFromCart, getTotalCartAmount, fetchCart, getTotalCartRows, updateCartQty, checkoutSession } from "../redux/shopSlice";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SkeletonRows from "../skeletons/CartItemRows";
import SkeletonBox from "../skeletons/CartItemBoxes";
import "./css/cart-items.css";

const CartItems = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const cartItems = useSelector((state) => state.cart.cartItems || []);
    const subtotal = useSelector(getTotalCartAmount);
    const totalRows = useSelector(getTotalCartRows);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [rowsPerPage] = useState(10);
    const S3Url = process.env.REACT_APP_S3_PATH;
    const isMobile = useMediaQuery("(max-width: 768px)");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await dispatch(fetchCart({ page, limit: rowsPerPage })).unwrap();
            } catch (error) {
                console.error("Error fetching cart:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [dispatch, page, rowsPerPage]);

    // Pagination handler
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleRemoveFromCart = async (cartId) => {
        await dispatch(removeFromCart(cartId)).unwrap();
    };

    // Checkout
    const handleCheckout = async (data) => {
        const { res, paymentMethod } = await dispatch(checkoutSession({ paymentMethod: data })).unwrap();
        if (res.success) {
            if (paymentMethod === 'COD') {
                navigate('/msg', {
                    state: { status: "success", msg: res.msg }
                });
            } else {
                if (res.url) {
                    window.location.href = res.url;
                } else {
                    toast.error("Something went wrong while redirecting to checkout.");
                }
            }
        }
    };

    const handleIncrease = (_id) => dispatch(updateCartQty({ cartId: _id, type: 'increase' }));
    const handleDecrease = (_id) => dispatch(updateCartQty({ cartId: _id, type: 'decrease' }));

    const renderPagination = () => {
        if (!loading && totalRows > 0) {
            return (
                <TablePagination
                    component="div"
                    count={totalRows || 1}
                    onPageChange={handleChangePage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10]}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} of ${count}`
                    }
                />
            );
        }
        return null;
    };

    const checkOutSummary = () => {
        if (totalRows > 0) {
            return (
                <Box className="cart-items-down">
                    <Box className="total">
                        <Typography variant="h5">Cart Totals</Typography>
                        <Divider />
                        <Box className="total-items">
                            <span>Subtotal</span>
                            <span>${subtotal.toFixed(2)}</span>
                        </Box>
                        <Box className="total-items">
                            <span>Shipping Fee</span>
                            <span>Free</span>
                        </Box>
                        <Divider />
                        <Box className="total-items">
                            <span>Total</span>
                            <span>${subtotal.toFixed(2)}</span>
                        </Box>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setCheckoutOpen(true)}
                        >
                            Proceed to Checkout
                        </Button>
                        <CheckoutModal
                            open={checkoutOpen}
                            onClose={() => setCheckoutOpen(false)}
                            onConfirm={(data) => {
                                handleCheckout(data);
                            }}
                        />
                    </Box>

                    <Box className="cart-items-promocode" sx={{ display: 'none' }}>
                        <Typography>If you have a promocode, enter it here</Typography>
                        <Box className="cart-items-promobox">
                            <input type="text" placeholder="Promo Code" />
                            <button>Apply</button>
                        </Box>
                    </Box>
                </Box>
            )
        };
        return null;
    };

    // ===================
    // Desktop Table View
    // ===================
    if (!isMobile) {
        return (
            <Box className="cart-items" sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Shopping Cart
                </Typography>

                <Paper sx={{ overflow: "hidden" }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#f8f8f8" }}>
                            <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Variatons</TableCell>
                                <TableCell align="center">Quantity</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell align="center">Remove</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <SkeletonRows />
                            ) : cartItems.length > 0 ? (
                                cartItems.map((item, i) => {
                                    const { productId, qty, unitPrice, primaryImage } = item;
                                    const { _id, name } = productId;
                                    return (
                                        <TableRow key={i} hover>
                                            <TableCell>
                                                <img src={S3Url + primaryImage} alt={name} className="cart-item-img" />
                                            </TableCell>
                                            <TableCell>
                                                <Link to={`/product/${_id}`} className="cart-item-link">
                                                    {name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>${unitPrice.toFixed(2)}</TableCell>
                                            <TableCell>
                                                {item.attributes.map((attr, j) => (
                                                    <Typography key={j} variant="body2">
                                                        {attr.name}: {attr.value}
                                                    </Typography>
                                                ))}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                                    <IconButton size="small" color="primary" onClick={() => handleDecrease(item._id)}>
                                                        <Remove fontSize="small" />
                                                    </IconButton>
                                                    <Typography variant="body1">{qty}</Typography>
                                                    <IconButton size="small" color="primary" onClick={() => handleIncrease(item._id)}>
                                                        <Add fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>${(unitPrice * qty).toFixed(2)}</TableCell>
                                            <TableCell align="center">
                                                <IconButton color="error" onClick={() => handleRemoveFromCart(item._id)}>
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No items in your cart.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {renderPagination()}
                </Paper>

                {checkOutSummary()}
            </Box>
        );
    }

    // ===================
    // Mobile Card View
    // ===================
    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" mb={2}>
                Shopping Cart
            </Typography>
            {loading ? (
                <SkeletonBox />
            ) : cartItems.length > 0 ? (
                cartItems.map((item, i) => {
                    const { productId, qty, unitPrice, primaryImage } = item;
                    const { _id, name } = productId;
                    return (
                        <Card key={i} sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Stack direction="row" spacing={2}>
                                    <img src={S3Url + primaryImage} alt={name} className="cart-item-img-mobile" />
                                    <Box sx={{ flex: 1 }}>
                                        <Link to={`/product/${_id}`} className="cart-item-link">
                                            <Typography variant="subtitle1">{name}</Typography>
                                        </Link>
                                        {item.attributes.map((attr, j) => (
                                            <Typography key={j} variant="body2">
                                                {attr.name}: {attr.value}
                                            </Typography>
                                        ))}
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Price: ${unitPrice}
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                            <IconButton size="small" color="primary" onClick={() => handleDecrease(item._id)}>
                                                <Remove fontSize="small" />
                                            </IconButton>
                                            <Typography>{qty}</Typography>
                                            <IconButton size="small" color="primary" onClick={() => handleIncrease(item._id)}>
                                                <Add fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                    <IconButton color="error" onClick={() => handleRemoveFromCart(item._id)}>
                                        <Delete />
                                    </IconButton>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })
            ) : (
                <Typography align="center" color="text.secondary">No Items In Your Cart.</Typography>
            )}

            {renderPagination()}

            {checkOutSummary()}
        </Box>
    );
};

export default CartItems;